import { styled } from '@mui/material/styles';
import { Box, Button, Typography, TableContainer, TableHead, TableRow, TableCell, TableSortLabel } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// ============================================
// Common Styles
// ============================================

export const PageContainer = styled(Box)({
  background: 'white',
  borderRadius: '5px',
  padding: '40px',
  width: '100%',
});

export const ModalPageContainer = styled(Box)(({ isModal }) => ({
  background: 'white',
  borderRadius: isModal ? '8px' : '5px',
  padding: isModal ? '0' : '40px',
  width: '100%',
  overflow: 'hidden',
}));

export const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

// 뒤로가기 아이콘과 타이틀이 붙어 보이지 않도록 gap 으로 간격을 준다.
export const BackLink = styled(Box)({
  textDecoration: 'none',
  color: '#3a3a3a',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  cursor: 'pointer',
  transition: 'opacity 160ms ease',
  '&:hover': { opacity: 0.7 },
});

export const HeaderActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const EditActionGroup = styled(Box)({
  display: 'flex',
  gap: '16px',
});

export const PageTitle = styled(Typography)({
  fontSize: '20px',
  lineHeight: '26px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.2px',
});

export const ListLink = styled(Typography)({
  color: '#4AB3BC',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: 550,
  cursor: 'pointer',
});

// ============================================
// Modal Header
// ============================================

export const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '56px',
  padding: '0 32px',
  backgroundColor: '#262626',
  borderRadius: '8px 8px 0 0',
});

export const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: '600',
  color: '#fff',
});

export const ModalCloseButton = styled(Button)({
  minWidth: '32px',
  width: '32px',
  height: '32px',
  padding: 0,
  color: '#fff',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
});

export const ModalBody = styled(Box)(({ isModal }) => ({
  padding: isModal ? '32px' : 0,
}));

// ============================================
// Form Layout
// ============================================

export const FormContainer = styled(Box)(({ isModal }) => ({
  display: 'flex',
  gap: '32px',
  background: 'white',
}));

export const ProfileSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '20px',
});

export const ProfileImageWrapper = styled(Box)({
  position: 'relative',
  cursor: 'pointer',
  '&:hover': { cursor: 'pointer' },
});

export const ProfileImage = styled('img')({
  width: '116px',
  height: '116px',
  objectFit: 'cover',
  borderRadius: '50%',
});

export const CameraIconWrapper = styled('div')({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: '#4AB3BC',
  color: '#fff',
  width: '36px',
  height: '36px',
  bottom: '0px',
  right: '0px',
  borderRadius: '50%',
});

export const UploadLabel = styled('label')({
  cursor: 'pointer',
});

export const HiddenFileInput = styled('input')({
  display: 'none',
});

export const CameraIcon = styled(PhotoCameraIcon)({
  fontSize: '20px',
  color: '#fff',
});

export const VerifiedIcon = styled(CheckCircleIcon)({
  color: '#4CAF50',
  fontSize: 28,
});

export const FormSection = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

export const TwoColumnLayout = styled(Box)({
  display: 'flex',
  gap: '40px',
});

export const FormColumn = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

// ============================================
// Form Field Labels
// ============================================

export const FieldLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '8px',
});

export const FieldLabel = styled(Typography)({
  color: '#3196A0',
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '-0.1px',
});

export const RequiredBadge = styled(Typography)({
  background: '#4AB3BC',
  padding: '0px 6px',
  borderRadius: '4px',
  marginLeft: '8px',
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 500,
});

export const FieldLabelWithMargin = styled(Typography)({
  color: '#3196A0',
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '-0.1px',
  marginBottom: '8px',
  marginTop: '16px',
});

export const FieldLabelSmallMargin = styled(Typography)({
  color: '#3196A0',
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '-0.1px',
  marginBottom: '2px',
  marginTop: '16px',
});

export const FieldLabelSmsMargin = styled(Typography)({
  color: '#3196A0',
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '-0.1px',
  marginBottom: '2px',
  marginTop: '8px',
});

// ============================================
// Phone Field
// ============================================

export const PhoneLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '8px',
  marginTop: '16px',
});

export const PhoneFieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const PhoneFieldWrapper = styled(Box)({
  flex: 1,
});

export const LinkedUserText = styled(Typography)({
  color: '#4b5563',
  fontSize: '14px',
  fontWeight: 500,
  marginTop: '4px',
});

// ============================================
// Memo Section
// ============================================

export const MemoSection = styled(Box)({
  marginTop: '10px',
});

export const MemoLabel = styled(Typography)({
  color: '#3196A0',
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '-0.1px',
  marginBottom: '8px',
});

export const AddressHintText = styled(Typography)({
  color: '#6b7280',
  fontSize: '12px',
  marginRight: '8px',
});

export const AddressFieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

// ============================================
// Buttons
// ============================================

export const PrimaryButton = styled(Button)({
  backgroundColor: '#4AB3BC',
  color: '#fff',
  '&:hover': { backgroundColor: '#3196A0' },
  height: 40,
  fontSize: '14px',
  fontWeight: 500,
  borderRadius: '8px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  padding: '0 20px',
});

export const DuplicateCheckButton = styled(Button)(({ verified }) => ({
  backgroundColor: verified ? '#d9d9d9' : '#4AB3BC',
  color: verified ? '#a6a6a6' : '#fff',
  '&:hover': { backgroundColor: verified ? '#d9d9d9' : '#3196A0' },
  height: 40,
  fontSize: '14px',
  fontWeight: 500,
  borderRadius: '8px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  padding: '0 20px',
}));

export const ButtonRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '16px',
  marginTop: '24px',
});

export const ButtonRowSpaceBetween = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '24px',
});

// ============================================
// Search Modal (User Search)
// ============================================

export const SearchModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 680,
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  padding: '24px',
});

export const SearchModalTitle = styled(Typography)({
  marginBottom: '16px',
  fontWeight: 600,
  fontSize: '18px',
});

export const SearchModalSubtitle = styled(Typography)({
  marginBottom: '16px',
  fontSize: '14px',
  color: '#666',
});

export const SearchTableContainer = styled(TableContainer)({
  maxHeight: 300,
});

export const SearchResultRow = styled(TableRow)({
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#e3f2fd' },
});

export const SearchModalFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '16px',
});

// ============================================
// Dup-check Modal — 회원 카드 리스트
// 이름·번호·생일만 보여주던 테이블로는 "타 지점의 그 회원"인지 판단할 수 없어 카드로 바꿨다.
// 지점 칩과 보유 수강권을 함께 보여주는 게 이 모달의 존재 이유다.
// ============================================

export const DupUserList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  maxHeight: 420,
  overflowY: 'auto',
  paddingRight: '4px',
});

export const DupUserCard = styled(Box)({
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '14px 16px',
  cursor: 'pointer',
  transition: 'border-color .15s, box-shadow .15s, background-color .15s',
  '&:hover': {
    borderColor: '#2563eb',
    backgroundColor: '#f8fafc',
    boxShadow: '0 2px 8px rgba(37,99,235,0.12)',
  },
});

export const DupUserCardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
});

export const DupUserName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#111827',
});

export const DupUserMeta = styled(Typography)({
  marginTop: '2px',
  fontSize: '13px',
  color: '#6b7280',
});

// status: VALID | EXPIRED | NONE
export const DupStatusBadge = styled(Box)(({ status }) => {
  const palette = {
    VALID: { color: '#047857', backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' },
    EXPIRED: { color: '#6b7280', backgroundColor: '#f3f4f6', borderColor: '#e5e7eb' },
    NONE: { color: '#9ca3af', backgroundColor: '#fff', borderColor: '#e5e7eb' },
  };
  const tone = palette[status] || palette.NONE;
  return {
    flexShrink: 0,
    padding: '2px 10px',
    borderRadius: '999px',
    border: `1px solid ${tone.borderColor}`,
    backgroundColor: tone.backgroundColor,
    color: tone.color,
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
  };
});

export const DupCardDivider = styled(Box)({
  height: '1px',
  backgroundColor: '#f0f1f3',
  margin: '12px 0 10px',
});

export const DupBranchChipRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
});

// current: 현재 로그인 지점이면 강조 — "우리 지점 회원인가"가 이 모달의 첫 질문이다
export const DupBranchChip = styled(Box)(({ current }) => ({
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: '2px 10px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: current ? 700 : 500,
  lineHeight: '20px',
  border: `1px solid ${current ? '#2563eb' : '#e5e7eb'}`,
  backgroundColor: current ? '#eff6ff' : '#f9fafb',
  color: current ? '#1d4ed8' : '#4b5563',
}));

export const DupAssetRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  marginTop: '6px',
  fontSize: '13px',
  color: '#1f2937',
});

export const DupAssetBranch = styled('span')({
  flexShrink: 0,
  minWidth: '84px',
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '#6b7280',
  fontSize: '12px',
});

export const DupAssetName = styled('span')({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const DupAssetCount = styled('span')({
  flexShrink: 0,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
});

export const DupAssetDate = styled('span')({
  flexShrink: 0,
  minWidth: '78px',
  textAlign: 'right',
  color: '#6b7280',
  fontSize: '12px',
  fontVariantNumeric: 'tabular-nums',
});

export const DupExpiredNote = styled(Box)({
  marginTop: '6px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '12px',
  color: '#9ca3af',
});

export const DupEmptyNote = styled(Box)({
  marginTop: '8px',
  fontSize: '13px',
  color: '#9ca3af',
});

// ============================================
// Delete Confirm Modal
// ============================================

export const DeleteConfirmModal = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '380px',
  height: '200px',
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: 'none',
});

export const DeleteConfirmText = styled(Typography)({
  fontSize: '18px',
  marginTop: '50px',
  marginLeft: '130px',
});

export const DeleteConfirmButton = styled(Button)({
  background: '#4AB3BC',
  color: '#fff',
  fontSize: '14px',
  borderRadius: '10px',
  marginTop: '40px',
  marginLeft: '135px',
  paddingLeft: '40px',
  paddingRight: '40px',
  '&:hover': { background: '#4AB3BC', color: '#fff' },
});

// ============================================
// List Page Styles
// ============================================

export const ListPageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '10px',
  padding: '40px',
  border: '1px solid #D9D9D9',
});

export const SearchRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const SearchInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const SearchLabel = styled(Typography)({
  width: '97px',
  fontSize: '13px',
  fontWeight: 500,
});

// ============================================
// New Filter Layout (Section Grid)
// ============================================

// 3열 그리드 — 1행(성별/연령대/전환확률)과 2행(방문경로/상담수단/운동목적)의
// 라벨·입력 좌우 위치가 세로로 정확히 맞아떨어지게 한다.
export const FilterGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: '32px',
  rowGap: '20px',
  marginTop: '24px',
  width: '100%',
});

// 기간처럼 한 행을 통째로 쓰는 블록
export const FilterBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  gridColumn: '1 / -1',
  minWidth: 0,
});

// 그리드 한 칸을 차지하는 블록 (성별 칩, 연령대·전환확률 드롭다운 등)
export const FilterBlockCompact = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  minWidth: 0,
});

// 필터 항목 라벨 — 공용(MemberFilter) 회색 라벨보다 진하게 잡아 항목 구분이 즉시 읽히게 한다.
export const FilterSectionTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '8px',
  borderBottom: '1px solid #D9D9D9',
  width: '100%',
});

export const FilterSectionTitleText = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
  color: '#1f2937',
  whiteSpace: 'nowrap',
});

export const FilterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: '27px',
  gap: '40px',
});

export const FilterSection = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const FilterLabel = styled(Typography)({
  width: '81px',
});

export const FilterDivider = styled(Box)({
  height: '20px',
  width: '1px',
  background: '#ddd',
});

export const PeriodRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: '32px',
});

export const PeriodLabel = styled(Typography)({
  width: '97px',
  fontSize: '13px',
  fontWeight: 500,
});

export const PeriodInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  columnGap: '20px',
});

export const FilterActionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '24px',
});

export const TableActionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '8px',
});

// 목록 상단 "총 N건"
export const TotalCountText = styled('span')({
  fontSize: '14px',
  fontWeight: 600,
  color: '#1f2937',
});

export const TotalCountNumber = styled('strong')({
  fontWeight: 700,
  color: '#4AB3BC',
});

export const PageSizeWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '24px',
  marginBottom: '8px',
  gap: '8px',
});

export const LoaderWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
});

// ============================================
// Table Styles
// ============================================

export const StyledTableContainer = styled(TableContainer)({
  marginTop: '10px',
  border: '1px solid #e8e8e8',
  borderRadius: '8px',
});

export const StyledTableHead = styled(TableHead)({
  background: '#F0F2F5',
});

export const StyledHeaderCell = styled(TableCell)({
  fontWeight: 700,
  fontSize: '13px',
  color: '#1f2937',
  padding: '12px 10px',
  whiteSpace: 'nowrap',
  borderRight: '1px solid #ddd',
  '&:last-child': {
    borderRight: 'none',
  },
});

export const StyledTableHeaderCell = StyledHeaderCell;

export const StyledTableRow = styled(TableRow)({
  height: '48px',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f9fafb',
  },
});

export const StyledBodyCell = styled(TableCell)({
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.5,
  color: '#1f2937',
  padding: '10px 10px',
  borderRight: '1px solid #f0f0f0',
  '&:last-child': {
    borderRight: 'none',
  },
});

export const StyledHeaderRow = styled(TableRow)({
  height: '48px',
});

// 전체 선택 체크박스 칸 — 내용 폭만큼만 차지한다
export const CheckboxHeaderCell = styled(StyledHeaderCell)({
  width: '48px',
});

// 검색 결과 없음 안내 — 회색이 너무 옅으면 "비어 있음"이 아니라 "로딩 중"으로 읽힌다
export const EmptyResultCell = styled(StyledBodyCell)({
  padding: '60px 0',
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
  borderRight: 'none',
});

export const SortLabel = styled(TableSortLabel)({
  '& .MuiTableSortLabel-icon': { opacity: 1 },
});

// ============================================
// Divider
// ============================================

export const FormDivider = styled('hr')({
  border: '1px solid #e1e1e1',
  marginTop: '24px',
});

// ============================================
// Required Note
// ============================================

export const RequiredNote = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '12px',
});

export const RequiredNoteSpan = styled('span')({
  fontSize: '11px',
  color: 'red',
  marginLeft: '10px',
});

// ============================================
// 리뉴얼 — 카드 레이아웃 / 운동목적 칩 / APP 연동
// ============================================

// FormContainer(flex)의 자식이라 width 를 주지 않으면 내용 폭까지만 늘어나
// 우측에 빈 공간이 남는다. 100% 로 잡아 카드가 화면 폭을 고르게 나눠 갖게 한다.
export const CardGrid = styled(Box)(({ columns = 3 }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  gap: '20px',
  width: '100%',
  alignItems: 'stretch',
}));

export const InfoCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  background: '#fff',
  border: '1px solid #e2e5e9',
  borderRadius: '10px',
  padding: '24px',
});

export const CardTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  paddingBottom: '14px',
  borderBottom: '1px solid #eef0f2',
});

export const CardTitleBar = styled('span')({
  width: '4px',
  height: '18px',
  borderRadius: '2px',
  background: '#4AB3BC',
});

export const CardTitleText = styled('span')({
  fontSize: '16px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.2px',
});

export const SectionSubText = styled('span')({
  fontSize: '12px',
  fontWeight: 500,
  color: '#6b7280',
});

export const FieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const FieldRow = styled(Box)({
  display: 'flex',
  gap: '16px',
  '& > *': { flex: 1, minWidth: 0 },
});

// 앞 항목은 내용만큼만 차지하고 남는 폭은 뒤 항목이 가져간다.
// 성별처럼 폭이 고정된 항목이 절반을 잡아 사이에 빈 공간이 생기는 걸 막는다.
export const FieldRowLead = styled(Box)({
  display: 'flex',
  gap: '16px',
  '& > *:first-of-type': { flex: '0 0 auto' },
  '& > *:not(:first-of-type)': { flex: 1, minWidth: 0 },
});

// 운동목적 칩
export const PurposeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const PurposeCount = styled('span')({
  marginLeft: 'auto',
  fontSize: '12px',
  color: '#3196A0',
  fontWeight: 700,
});

export const PurposeChips = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

export const PurposeChip = styled('div')(({ isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '34px',
  padding: '0 14px',
  borderRadius: '17px',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  fontSize: '13px',
  transition: 'border-color 160ms ease, background 160ms ease',
  background: isActive ? '#E9F6F7' : '#fff',
  border: `1px solid ${isActive ? '#4AB3BC' : '#d9d9d9'}`,
  color: isActive ? '#4AB3BC' : '#374151',
  fontWeight: isActive ? 700 : 500,
  '&:hover': { borderColor: '#4AB3BC' },
}));

export const PurposeChipMark = styled('span')({
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1,
});

// ============================================
// 상담 이력 로그
// ============================================

export const ConsultLogList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

export const ConsultLogItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '14px 16px',
  background: '#F7F8FA',
  border: '1px solid #eef0f2',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background 160ms ease, border-color 160ms ease',
  '&:active': {
    background: '#EDEFF2',
  },
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      background: '#F1F3F6',
      borderColor: '#dfe3e8',
    },
  },
});

// 상담 결과 뱃지 — 결과가 없던 시절 데이터는 예정으로 본다.
export const ConsultLogResult = styled('span', {
  shouldForwardProp: prop => prop !== 'resultColor',
})(({ resultColor }) => ({
  fontSize: '11px',
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: '10px',
  background: resultColor,
  color: '#fff',
  whiteSpace: 'nowrap',
}));

export const ConsultLogHead = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ConsultLogDate = styled('span')({
  fontSize: '13px',
  fontWeight: 700,
  color: '#111827',
});

export const ConsultLogMethod = styled('span')({
  fontSize: '11px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '10px',
  background: '#E9F6F7',
  color: '#4AB3BC',
});

export const ConsultLogManager = styled('span')({
  marginLeft: 'auto',
  fontSize: '12px',
  fontWeight: 500,
  color: '#6b7280',
});

export const ConsultLogContent = styled('div')({
  fontSize: '13px',
  color: '#374151',
  lineHeight: 1.7,
  whiteSpace: 'pre-wrap',
});

export const EmptyText = styled('span')({
  fontSize: '13px',
  fontWeight: 500,
  color: '#6b7280',
});

// APP 연동 토글 박스
export const AppLinkBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '14px 16px',
  background: '#F7FBFB',
  border: '1px solid #d7e5e6',
  borderRadius: '8px',
});

export const AppLinkBoxTitle = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  whiteSpace: 'nowrap',
  '& svg': { fontSize: '18px', color: '#4AB3BC' },
});

export const AppLinkBoxHint = styled('span')({
  fontSize: '12px',
  color: '#6b7280',
});

export const AppLinkSwitch = styled('div')(({ isOn }) => ({
  marginLeft: 'auto',
  width: '48px',
  height: '26px',
  borderRadius: '13px',
  position: 'relative',
  cursor: 'pointer',
  flexShrink: 0,
  background: isOn ? '#4AB3BC' : '#d9d9d9',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '3px',
    left: isOn ? '25px' : '3px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
    transition: 'left 0.15s ease',
  },
}));

export const AppStatusBadge = styled('span')(({ isLinked }) => ({
  fontSize: '12px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '10px',
  background: isLinked ? '#E9F6F7' : '#F1F1F3',
  color: isLinked ? '#3196A0' : '#6b7280',
}));

// ============================================
// APP 연동 팝업
// ============================================

export const AppLinkContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '420px',
  maxHeight: '90vh',
  overflowY: 'auto',
  background: '#fff',
  borderRadius: '8px',
  boxShadow: '0 16px 48px rgba(0,0,0,0.24)',
  outline: 'none',
});

export const AppLinkHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '52px',
  padding: '0 20px',
  background: '#262626',
});

export const AppLinkHeaderTitle = styled('div')({
  fontSize: '18px',
  fontWeight: 600,
  color: '#fff',
});

export const AppLinkHeaderClose = styled('div')({
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '18px',
  cursor: 'pointer',
});

export const AppLinkBody = styled(Box)({
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
});

export const AppLinkDescription = styled('div')({
  fontSize: '13px',
  color: '#6b7280',
  lineHeight: 1.7,
});

export const AppLinkPanel = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  padding: '16px',
  background: '#F7FBFB',
  border: '1px solid #d7e5e6',
  borderRadius: '10px',
});

export const AppLinkTabs = styled(Box)({
  display: 'flex',
  gap: '6px',
  padding: '4px',
  background: '#EAEFF0',
  borderRadius: '8px',
});

export const AppLinkTab = styled('div', { shouldForwardProp: prop => prop !== 'isActive' })(({ isActive }) => ({
  flex: 1,
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  fontSize: '14px',
  cursor: 'pointer',
  background: isActive ? '#fff' : 'transparent',
  color: isActive ? '#4AB3BC' : '#767676',
  fontWeight: isActive ? 700 : 400,
}));

export const AppLinkGrid = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
});

export const AppLinkFieldGroup = styled(Box)(({ span }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  ...(span === 2 ? { gridColumn: 'span 2' } : {}),
}));

export const AppLinkFieldLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const AppLinkInputRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& > input': { flex: 1 },
});

export const AppLinkInput = styled('input')({
  width: '100%',
  minWidth: 0,
  height: '40px',
  flexShrink: 0,
  border: '1px solid rgba(133,133,133,0.5)',
  borderRadius: '4px',
  padding: '0 14px',
  fontSize: '15px',
  fontWeight: 700,
  color: '#3a3a3a',
  background: '#fff',
});

export const AppLinkDupButton = styled('button')({
  height: '40px',
  flexShrink: 0,
  padding: '0 12px',
  border: '1px solid #4AB3BC',
  borderRadius: '8px',
  background: '#fff',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const AppLinkSearchButton = styled('button')({
  height: '40px',
  flexShrink: 0,
  padding: '0 16px',
  border: 'none',
  borderRadius: '8px',
  background: '#4AB3BC',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
});

export const AppLinkHint = styled('div')(({ span }) => ({
  fontSize: '12px',
  color: '#5f6b6c',
  lineHeight: 1.7,
  ...(span === 2 ? { gridColumn: 'span 2' } : {}),
}));

export const AppLinkResultCount = styled('div')({
  fontSize: '12px',
  color: '#8a8f98',
});

export const AppLinkAccountList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxHeight: '200px',
  overflowY: 'auto',
});

export const AppLinkAccountCard = styled(Box, { shouldForwardProp: prop => prop !== 'isActive' })(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  borderRadius: '8px',
  cursor: 'pointer',
  background: isActive ? '#F3FAFB' : '#fff',
  border: `1px solid ${isActive ? '#4AB3BC' : '#e6e8eb'}`,
}));

export const AppLinkAccountId = styled('div')({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
});

export const AppLinkAccountSub = styled('div')({
  fontSize: '12px',
  color: '#767676',
  whiteSpace: 'nowrap',
});

export const AppLinkAccountMark = styled('div')({
  marginLeft: 'auto',
  fontSize: '15px',
  color: '#4AB3BC',
});

export const AppLinkTermsSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const AppLinkTermsTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const AppLinkTermsAllRow = styled('label', { shouldForwardProp: prop => prop !== 'isActive' })(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  height: '48px',
  padding: '0 16px',
  borderRadius: '8px',
  cursor: 'pointer',
  background: isActive ? '#F3FAFB' : '#fff',
  border: `1px solid ${isActive ? '#4AB3BC' : '#d9d9d9'}`,
  '& span': {
    fontSize: '15px',
    fontWeight: 600,
    color: isActive ? '#4AB3BC' : '#3a3a3a',
  },
  '& input': { width: '20px', height: '20px', accentColor: '#4AB3BC', margin: 0 },
}));

export const AppLinkTermsRow = styled('label')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minHeight: '44px',
  padding: '4px 0',
  '& input': { width: '18px', height: '18px', accentColor: '#4AB3BC', margin: 0, cursor: 'pointer' },
});

export const AppLinkTermsLabel = styled('span')({
  fontSize: '14px',
  color: '#3a3a3a',
  lineHeight: 1.6,
});

export const AppLinkTermsBadge = styled('span')({
  color: '#4AB3BC',
  fontWeight: 600,
});

export const AppLinkFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px 20px',
  background: '#F7F8FA',
  borderTop: '1px solid #e6e8eb',
});

export const AppLinkFooterHint = styled('div')(({ isError }) => ({
  fontSize: '12px',
  color: isError ? '#ef0c23' : '#8a8f98',
}));

export const AppLinkFooterActions = styled(Box)({
  marginLeft: 'auto',
  display: 'flex',
  gap: '12px',
});

// ============================================
// 중복 번호 경고 모달
// ============================================

export const DupModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '480px',
  background: '#fff',
  borderRadius: '12px',
  boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
  padding: '26px 28px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  outline: 'none',
});

export const DupModalHeadRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
});

export const DupModalIcon = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: '#FFF1E8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: '#F2711C',
  fontSize: '18px',
  fontWeight: 700,
});

export const DupModalTitle = styled('div')({
  fontSize: '17px',
  fontWeight: 700,
  color: '#262626',
});

export const DupModalDesc = styled('div')({
  fontSize: '13px',
  color: '#6b7280',
  lineHeight: 1.7,
});

export const DupModalList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  background: '#F7F8FA',
  borderRadius: '10px',
  maxHeight: '220px',
  overflowY: 'auto',
});

export const DupModalItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 12px',
  background: '#fff',
  border: '1px solid #e6e8eb',
  borderRadius: '8px',
});

export const DupModalItemName = styled('span')({
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  whiteSpace: 'nowrap',
});

export const DupModalItemTag = styled('span')({
  fontSize: '11px',
  fontWeight: 600,
  padding: '2px 7px',
  borderRadius: '10px',
  background: '#E9F6F7',
  color: '#4AB3BC',
});

export const DupModalItemSub = styled('div')({
  fontSize: '12px',
  color: '#767676',
  whiteSpace: 'nowrap',
});

export const DupModalActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
});

// ============================================
// 리스트 통계 카드
// ============================================

// 카드 개수가 바뀌어도(앱연동 카드 숨김 등) 남는 칸 없이 가로를 채운다.
export const SummaryCardRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
});

export const SummaryCard = styled(Box)({
  background: '#fff',
  border: '1px solid #e2e5e9',
  borderRadius: '10px',
  padding: '20px 22px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

export const SummaryCardLabel = styled('span')({
  fontSize: '13px',
  fontWeight: 500,
  color: '#4b5563',
});

export const SummaryCardValue = styled('span')({
  fontSize: '26px',
  fontWeight: 700,
  lineHeight: 1.2,
  color: '#111827',
});

export const AppLinkedBadge = styled('span')(({ isLinked }) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  background: isLinked ? '#E9F6F7' : '#F1F1F3',
  color: isLinked ? '#3196A0' : '#6b7280',
}));

// 상세 헤더 — 이름·구분·연락처를 한 줄로 보여준다
export const DetailTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

export const DetailName = styled('span')({
  fontSize: '20px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.2px',
});

export const DetailTypeBadge = styled('span')({
  fontSize: '12px',
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: '10px',
  background: '#EEF0F3',
  color: '#4b5563',
});

export const DetailPhone = styled('span')({
  fontSize: '14px',
  fontWeight: 500,
  color: '#4b5563',
});

export const AppLinkBoxButton = styled('button')(({ variant }) => ({
  marginLeft: 'auto',
  height: '34px',
  padding: '0 14px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  background: variant === 'unlink' ? '#fff' : '#4AB3BC',
  border: `1px solid ${variant === 'unlink' ? '#d9d9d9' : '#4AB3BC'}`,
  color: variant === 'unlink' ? '#6b7280' : '#fff',
}));
